/**
 * Structured Logging Utility
 *
 * Provides structured logging with log levels, environment-based filtering,
 * optional structured output format (JSON in production, readable in dev),
 * and PostHog error tracking integration.
 */

export enum LogLevel {
  DEBUG = 0,
  INFO = 1,
  WARN = 2,
  ERROR = 3,
}

/**
 * Get PostHog instance (helper to avoid circular dependencies)
 * PostHog is initialized globally and available on window.posthog
 */
function getPostHog(): unknown {
  try {
    // Try to get PostHog from window (it's initialized globally)
    if (typeof window !== "undefined") {
      const win = window as unknown as { posthog?: unknown };
      if (win.posthog) {
        return win.posthog;
      }
    }
  } catch {
    // Ignore errors if window is not available
  }
  return null;
}

interface LogEntry {
  level: LogLevel;
  message: string;
  timestamp: string;
  context?: Record<string, unknown>;
  error?: Error;
}

class Logger {
  private currentLevel: LogLevel;
  private isDevelopment: boolean;

  constructor() {
    // Determine log level from environment
    const envLevel = import.meta.env.VITE_LOG_LEVEL?.toUpperCase() || "";
    this.currentLevel = this.parseLogLevel(envLevel);
    this.isDevelopment = import.meta.env.DEV;
  }

  private parseLogLevel(level: string): LogLevel {
    switch (level) {
      case "DEBUG":
        return LogLevel.DEBUG;
      case "INFO":
        return LogLevel.INFO;
      case "WARN":
        return LogLevel.WARN;
      case "ERROR":
        return LogLevel.ERROR;
      default:
        // In development, default to DEBUG. In production, default to INFO.
        return this.isDevelopment ? LogLevel.DEBUG : LogLevel.INFO;
    }
  }

  private shouldLog(level: LogLevel): boolean {
    return level >= this.currentLevel;
  }

  private formatLog(entry: LogEntry): string {
    const { level, message, timestamp, context, error } = entry;

    if (this.isDevelopment) {
      // Human-readable format for development
      const levelName = LogLevel[level];
      const contextStr = context ? ` ${JSON.stringify(context)}` : "";
      const errorStr = error ? `\nError: ${error.message}\n${error.stack}` : "";
      return `[${timestamp}] ${levelName}: ${message}${contextStr}${errorStr}`;
    } else {
      // JSON format for production
      return JSON.stringify({
        level: LogLevel[level],
        levelName: LogLevel[level],
        message,
        timestamp,
        ...(context && { context }),
        ...(error && {
          error: {
            message: error.message,
            stack: error.stack,
            name: error.name,
          },
        }),
      });
    }
  }

  private log(
    level: LogLevel,
    message: string,
    context?: Record<string, unknown>,
    error?: Error
  ): void {
    if (!this.shouldLog(level)) {
      return;
    }

    const entry: LogEntry = {
      level,
      message,
      timestamp: new Date().toISOString(),
      ...(context && { context }),
      ...(error && { error }),
    };

    const formatted = this.formatLog(entry);

    // Route to appropriate console method
    switch (level) {
      case LogLevel.DEBUG:
        console.debug(formatted);
        break;
      case LogLevel.INFO:
        console.info(formatted);
        break;
      case LogLevel.WARN:
        console.warn(formatted);
        break;
      case LogLevel.ERROR:
        console.error(formatted);
        break;
    }
  }

  /**
   * Log a debug message (only shown in development or if LOG_LEVEL=DEBUG)
   */
  debug(message: string, context?: Record<string, unknown>): void {
    this.log(LogLevel.DEBUG, message, context);
  }

  /**
   * Log an info message
   */
  info(message: string, context?: Record<string, unknown>): void {
    this.log(LogLevel.INFO, message, context);
  }

  /**
   * Log a warning message
   */
  warn(message: string, context?: Record<string, unknown>): void {
    this.log(LogLevel.WARN, message, context);
  }

  /**
   * Log an error message and send to PostHog if available
   */
  error(
    message: string,
    error?: Error | unknown,
    context?: Record<string, unknown>
  ): void {
    let errorObj: Error | undefined;

    if (error instanceof Error) {
      errorObj = error;
    } else if (error) {
      errorObj = new Error(String(error));
    }

    this.log(LogLevel.ERROR, message, context, errorObj);

    // Send to PostHog for error tracking
    if (errorObj) {
      const posthog = getPostHog();
      if (
        posthog &&
        typeof posthog === "object" &&
        "captureException" in posthog &&
        typeof (posthog as { captureException: unknown }).captureException ===
          "function"
      ) {
        try {
          (
            posthog as {
              captureException: (
                error: Error,
                props?: Record<string, unknown>
              ) => void;
            }
          ).captureException(errorObj, {
            $exception_message: message,
            $exception_type: errorObj.constructor.name,
            ...context,
          });
        } catch (err) {
          // If PostHog capture fails, don't break the app

          console.warn("Failed to send error to PostHog:", err);
        }
      }
    }
  }

  /**
   * Create a child logger with additional context
   */
  child(context: Record<string, unknown>): Logger {
    const childLogger = new Logger();
    const originalLog = childLogger.log.bind(childLogger);

    // Override log method to merge context
    childLogger.log = (
      level: LogLevel,
      message: string,
      logContext?: Record<string, unknown>,
      error?: Error
    ) => {
      const mergedContext = { ...context, ...logContext };
      originalLog(level, message, mergedContext, error);
    };

    return childLogger;
  }

  /**
   * Set the log level at runtime
   */
  setLevel(level: LogLevel): void {
    this.currentLevel = level;
  }
}

// Export a singleton instance
export const logger = new Logger();

// Export Logger class for creating custom loggers if needed
export { Logger };
